<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.DIALOG.PROMPT_REQUESTED"
    :handler="
      (data) => {
        $refs.modal.show();
      }
    "
  />

  <DialogComponent
    ref="modal"
    type="modal"
    mainClass="fixed z-[61]"
    @closed="
      () => {
        if (currentResolve) {
          currentResolve({ accepted: false });
          currentResolve = null;
        }
      }
    "
  >
    <div class="py-2">
      <div class="text-xl font-bold mb-5" v-if="promptData.title">
        {{ promptData.title }}
      </div>

      <div v-if="promptData.message" class="mb-5">
        {{ promptData.message }}
      </div>

      <div
        v-if="promptData.options && promptData.options.length > 0"
        class="space-y-3"
      >
        <div
          v-for="(option, index) in promptData.options"
          :key="index"
          @click="selectOption(option)"
          class="flex items-center gap-3 p-4 rounded-xl bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors"
        >
          <div
            class="w-5 h-5 rounded-full border-2 border-gray-400 flex items-center justify-center"
          >
            <div
              v-if="selectedOption && selectedOption.name === option.name"
              class="w-3 h-3 bg-primary rounded-full"
            ></div>
          </div>
          <div class="flex-1">
            <div class="font-medium">
              {{
                formatDisplay
                  ? formatDisplay(option)
                  : option.title || option.name
              }}
            </div>
            <div v-if="option.description" class="text-sm text-gray-600">
              {{ option.description }}
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="promptData.withInput"
        class="mb-5"
      >
        <input
          v-model="inputValue"
          type="text"
          :placeholder="promptData.placeholder || ''"
          class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:border-primary"
        />
      </div>
    </div>

    <div class="flex gap-4 mt-5 justify-end">
      <button
        class="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
        @click="cancelPrompt"
      >
        {{ promptData.cancelText || 'Annuler' }}
      </button>
      <button
        v-if="promptData.options && promptData.options.length > 0"
        class="px-6 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!selectedOption"
        @click="confirmPrompt"
      >
        {{ promptData.successText || 'Confirmer' }}
      </button>
      <button
        v-else-if="promptData.withInput"
        class="px-6 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
        @click="confirmPrompt"
      >
        {{ promptData.successText || 'Confirmer' }}
      </button>
      <button
        v-else
        class="px-6 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
        @click="confirmPrompt"
      >
        {{ promptData.successText || 'Confirmer' }}
      </button>
    </div>
  </DialogComponent>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const promptData = ref({
  title: '',
  message: '',
  options: [],
  withInput: false,
  placeholder: '',
  successText: '',
  cancelText: 'Annuler',
  formatDisplay: null
});
const selectedOption = ref(null);
const inputValue = ref('');
let currentResolve = null;
let currentReject = null;

const selectOption = (option) => {
  selectedOption.value = option;
};

const confirmPrompt = () => {
  if (currentResolve) {
    if (promptData.value.options && promptData.value.options.length > 0) {
      currentResolve({
        accepted: true,
        data: selectedOption.value
      });
    } else if (promptData.value.withInput) {
      currentResolve({
        accepted: true,
        data: inputValue.value
      });
    } else {
      currentResolve({
        accepted: true
      });
    }
    currentResolve = null;
    currentReject = null;
  }
  resetPrompt();
  proxy.$refs.modal?.hide();
};

const cancelPrompt = () => {
  if (currentResolve) {
    currentResolve({ accepted: false });
    currentResolve = null;
    currentReject = null;
  }
  resetPrompt();
  proxy.$refs.modal?.hide();
};

const resetPrompt = () => {
  promptData.value = {
    title: '',
    message: '',
    options: [],
    withInput: false,
    placeholder: '',
    successText: '',
    cancelText: 'Annuler',
    formatDisplay: null
  };
  selectedOption.value = null;
  inputValue.value = '';
};

const customPrompt = async (config) => {
  return new Promise((resolve, reject) => {
    currentResolve = resolve;
    currentReject = reject;

    promptData.value = {
      title: config.title || '',
      message: config.message || '',
      placeholder: config.placeholder || '',
      successText: config.successText || 'Confirmer',
      cancelText: config.cancelText || 'Annuler',
      options: [],
      withInput: config.withInput ?? true,
      formatDisplay: config.formatDisplay || null
    };

    proxy.$refs.modal.show();
  });
};

const choicePrompt = async (config) => {
  return new Promise((resolve, reject) => {
    currentResolve = resolve;
    currentReject = reject;

    promptData.value = {
      title: config.title || '',
      message: config.message || '',
      options: config.options || [],
      placeholder: config.placeholder || '',
      successText: config.successText || 'Confirmer',
      cancelText: config.cancelText || 'Annuler',
      withInput: false,
      formatDisplay: config.formatDisplay || null
    };

    selectedOption.value = null;
    proxy.$refs.modal.show();
  });
};

// Exposer les fonctions globalement
onMounted(() => {
  window.customPrompt = customPrompt;
  window.choicePrompt = choicePrompt;
});

onBeforeUnmount(() => {
  delete window.customPrompt;
  delete window.choicePrompt;
});
</script>
